'use client';
import React from 'react';

const stats = [
  { value: '40+', label: 'Clients secured', sub: 'Across regulated industries globally' },
  { value: '12', label: 'Frameworks mapped', sub: 'ISO 27001 · SOC 2 · NIST · PCI-DSS · NIS2 · CIS · DORA · GDPR · HIPAA · OWASP · MITRE ATT&CK · EU AI Act' },
  { value: '8', label: 'Programmes delivered', sub: 'Accelerator, IaC, Security Focus, Bespoke & more' },
];

const badges = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    label: 'Senior engineers only',
    detail: 'No juniors. No box-ticking.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    label: 'Continuous coverage',
    detail: '365 days, not point-in-time audits.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
    label: 'Auditor-aligned delivery',
    detail: 'Evidence-ready. No unnecessary work.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    label: 'Rapid time-to-value',
    detail: 'Embedded within days, not months.',
  },
];

const pressMentions = [
  {
    outlet: 'SC Media',
    quote: 'TechSonar\'s embedded model is closing the gap between compliance theatre and real security posture.',
    tag: 'DevSecOps',
    href: undefined, // no destination exists yet; React omits href when undefined
  },
  {
    outlet: 'InfoSecurity Magazine',
    quote: 'A pragmatic approach to framework alignment that regulated firms are increasingly demanding.',
    tag: 'Compliance',
    href: undefined, // no destination exists yet; React omits href when undefined
  },
  {
    outlet: 'The Register',
    quote: 'Cutting through the noise: senior-only delivery and continuous coverage set TechSonar apart.',
    tag: 'Industry',
    href: undefined, // no destination exists yet; React omits href when undefined
  },
];

export default function SocialProofSection() {
  return (
    <section className="bg-[#050a0f] border-t border-[#222] py-20 lg:py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="orb orb-blue" style={{ width: '500px', height: '500px', top: '-80px', right: '5%', opacity: 0.35 }} />
        <div className="orb orb-cyan" style={{ width: '350px', height: '350px', bottom: '-60px', left: '8%', opacity: 0.25 }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#80edfa]/15 to-transparent" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">

        {/* ── STATS STRIP ── */}
        <div className="mb-20">
          <p className="fade-in-up font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">
            By the numbers
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-[#222]">
            {stats?.map((s, i) => (
              <div
                key={i}
                className={`py-10 lg:py-12 shimmer-hover transition-all duration-300 ${
                  i < stats?.length - 1 ? 'border-b lg:border-b-0 lg:border-r border-[#222]' : ''
                } ${i > 0 ? 'lg:pl-12' : ''} ${i < stats?.length - 1 ? 'lg:pr-12' : ''}`}
              >
                <div className="flex flex-col gap-3">
                  <div className="stat-number gradient-text-animate">{s?.value}</div>
                  <p className="font-mono-brand text-[0.7rem] text-[#666] uppercase tracking-widest">{s?.label}</p>
                  <p className="text-sm text-[#aaa] leading-relaxed">{s?.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── TRUST BADGES ── */}
        <div className="mb-20">
          <p className="fade-in-up font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-8">
            Why clients trust us
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {badges?.map((b, i) => (
              <div
                key={i}
                className="bg-[#050a0f] p-8 group hover:bg-[#0d1117] transition-colors duration-300 flex flex-col gap-4"
              >
                <div className="w-10 h-10 rounded border border-[#222] flex items-center justify-center text-[#80edfa] group-hover:border-[#80edfa]/40 transition-colors duration-300">
                  {b?.icon}
                </div>
                <div>
                  <p className="text-[#f5f5f8] text-sm font-medium mb-1">{b?.label}</p>
                  <p className="text-[#666] text-xs leading-relaxed">{b?.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── PRESS MENTIONS ── */}
        <div>
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-0 lg:items-end justify-between mb-10">
            <div>
              <p className="fade-in-up font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">
                Press &amp; coverage
              </p>
              <h2 className="font-heading text-[clamp(1.6rem,3vw,2.5rem)] text-[#f5f5f8] font-medium leading-tight">
                What the industry is saying.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-l border-[#222]">
            {pressMentions?.map((p, i) => (
              <a
                key={i}
                href={p?.href}
                className="block p-8 border-b border-r border-[#222] group hover:bg-[#0d1117] transition-colors duration-200"
              >
                <div className="flex flex-col gap-5 h-full">
                  {/* Outlet name */}
                  <div className="flex items-center justify-between">
                    <span className="font-mono-brand text-[0.65rem] uppercase tracking-widest text-[#80edfa]">
                      {p?.outlet}
                    </span>
                    <span className="font-mono-brand text-[0.6rem] uppercase tracking-widest text-[#444] border border-[#222] px-2 py-0.5">
                      {p?.tag}
                    </span>
                  </div>
                  {/* Quote */}
                  <div className="flex-1">
                    <div className="w-4 h-px bg-[#80edfa]/40 mb-4" />
                    <p className="text-[#aaa] text-sm leading-relaxed italic group-hover:text-[#f5f5f8] transition-colors duration-200">
                      "{p?.quote}"
                    </p>
                  </div>
                  {/* Read link */}
                  <div className="flex items-center gap-2 text-[#555] group-hover:text-[#80edfa] transition-colors duration-200">
                    <span className="font-mono-brand text-[0.6rem] uppercase tracking-widest">Read article</span>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
